import { Component, OnInit, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { MenuItem } from 'primeng/api';
import { Menubar } from 'primeng/menubar';
import { Router } from '@angular/router';
import {
  NavigationDropdown,
  NavigationItem,
} from '../../interfaces/navigation-item.interface';
import { RoleGuardService as RoleGuard } from '../../../app/pages/auth/helpers/role-guard.service';

/** Maps app icon names to PrimeIcons CSS classes for smooth menubar display */
const ICON_MAP: Record<string, string> = {
  home: 'pi pi-home',
  archive: 'pi pi-archive',
  edit: 'pi pi-pencil',
  badge: 'pi pi-id-card',
  manage_accounts: 'pi pi-users',
  production_quantity_limits: 'pi pi-box',
};

const DEFAULT_ICON = 'pi pi-circle';

@Component({
  selector: 'vex-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  standalone: false,
})
export class NavigationComponent implements OnInit, OnChanges {
  @Input() inToolbar = false;

  menuItems: MenuItem[] = [];
  private userRole: string;

  constructor(
    private navigationService: NavigationService,
    private roleGuard: RoleGuard,
    private router: Router
  ) {
    this.userRole = this.roleGuard.getRole();
  }

  ngOnInit(): void {
    this.buildMenuItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inToolbar']) {
      this.buildMenuItems();
    }
  }
  
  private buildMenuItems(): void {
    const items = this.filterItemsByRole(this.navigationService.items ?? []);
    this.menuItems = this.toPrimeNgMenuItems(items);
  }

  private filterItemsByRole(items: NavigationItem[]): NavigationItem[] {
    if (!items?.length) return [];
    return items
      .filter((item) => {
        const role = item.role;
        if (!role || (Array.isArray(role) && role.length === 0)) return true;
        const roles = Array.isArray(role) ? role : [role];
        return roles.includes(this.userRole);
      })
      .map((item) => {
        if (this.navigationService.isDropdown(item) && item.children?.length) {
          return {
            ...item,
            children: this.filterItemsByRole(item.children),
          } as NavigationDropdown;
        }
        return item;
      });
  }

  private toPrimeNgMenuItems(items: NavigationItem[]): MenuItem[] {
    return items.map((item) => this.toPrimeNgMenuItem(item));
  }

  private toPrimeNgMenuItem(item: NavigationItem): MenuItem {
    const label = item.label;
    const icon = this.getPrimeIcon(item.icon);

    if (this.navigationService.isLink(item)) {
      const route = item.route;
      const routerLink = typeof route === 'string' ? [route] : Array.isArray(route) ? route : null;
      return {
        label,
        icon,
        ...(routerLink && { routerLink }),
      };
    }

    if (this.navigationService.isDropdown(item)) {
      const children = (item.children ?? []).map((child) => this.toPrimeNgMenuItem(child));
      const route = item.route;
      const routerLink =
        route && typeof route === 'string' ? [route] : Array.isArray(route) ? route : undefined;
      const hasChildren = children.length > 0;
      return {
        label,
        icon,
        items: hasChildren ? children : undefined,
        ...(hasChildren
          ? (routerLink
            ? {
                command: () => {
                  this.router.navigate(routerLink);
                },
              }
            : {})
          : (routerLink ? { routerLink } : {})),
      };
    }

    return { label, icon };
  }

  private getPrimeIcon(icon?: string): string {
    const key = (icon ?? '').toString();
    if (ICON_MAP[key]) return ICON_MAP[key];
    if (key.startsWith('pi ')) return key;
    if (key.includes('chrome-reader-mode') || key.includes('reader')) return 'pi pi-book';
    if (key.includes('assignment')) return 'pi pi-list';
    if (key.includes('settings')) return 'pi pi-cog';
    return DEFAULT_ICON;
  }
}
